import React, { FC, RefObject, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import classNames from "classnames";
import { clamp } from "lodash";
import toast from "react-hot-toast";
import { IComment, toMmSs } from "./constants";
import { useVideoFeedback } from "./useVideoFeedback";
import { MentionTextarea } from "../../../../components/Mentions/MentionTextarea";
import { TaggableUser, serializeMentions } from "../../../../components/Mentions/MentionText";
import { commentTaggableUsers } from "../../../../api/endpoints";
import { Button } from "../../../../components/Button/Button";
import { IconClock, IconCross, IconTimer } from "../../../../utils/icons";
import { useUser } from "../../../../hooks/useUser";
import { FeedbackCriteria } from "./FeedbackCriteria";
import { VideoTimeline } from "../../../../components/VideoTimeline/VideoTimeline";

interface Props {
    videoRef: RefObject<HTMLVideoElement>;
}

const MIN_RANGE_SECONDS = 0.5;

export const CreateCommentPanel: FC<Props> = ({ videoRef }) => {
    const { user } = useUser();
    const initialComment = {
        message: "",
        user: {
            userid: user?.userid || 0,
            displayName: user?.displayName || ""
        }
    };

    const { onCreateComment, isCommentLoading, guestName, onGuestNameChange, project } =
        useVideoFeedback();
    const isGuestMode = onGuestNameChange !== undefined;
    const [picked, setPicked] = useState<TaggableUser[]>([]);

    const [comment, setComment] = useState<Partial<IComment>>(initialComment);

    const [isTimestampActive, setIsTimestampActive] = useState(false);
    const [startTime, setStartTime] = useState(0);
    const [endTime, setEndTime] = useState(0);

    // Track duration locally from the element so this panel re-renders when
    // metadata arrives, without any playback-time state living in context.
    const [duration, setDuration] = useState(NaN);
    const hasDuration = Number.isFinite(duration) && duration > 0;

    useEffect(() => {
        const videoEl = videoRef.current;
        if (!videoEl) return undefined;
        const updateDuration = () => setDuration(videoEl.duration);
        updateDuration();
        videoEl.addEventListener("loadedmetadata", updateDuration);
        videoEl.addEventListener("durationchange", updateDuration);
        return () => {
            videoEl.removeEventListener("loadedmetadata", updateDuration);
            videoEl.removeEventListener("durationchange", updateDuration);
        };
    }, [videoRef]);

    // If the source changes under an open picker, drop a now-invalid range.
    useEffect(() => {
        if (isTimestampActive && hasDuration && endTime > duration) {
            setIsTimestampActive(false);
        }
    }, [duration]);

    const editComment = (val: Partial<IComment>) => {
        setComment({ ...comment, ...val });
    };

    const onCancelTime = () => {
        setIsTimestampActive(false);
    };

    const submitComment = async () => {
        if (!isGuestMode && !user?.userid) return;

        const payload = {
            ...comment,
            message: serializeMentions(comment.message || "", picked),
            user: {
                userid: user?.userid ?? 0,
                displayName: user?.displayName || ""
            }
        };

        if (isTimestampActive) {
            const start = Math.floor(startTime);
            payload.timestamp = start;
            // Ceil the end so sub-second ranges survive integer storage — the
            // API coerces a falsy timestamp_end to null, dropping the range.
            // Lower bound start + 1 keeps the range non-empty; it never
            // conflicts with the cap since start <= duration - minRange.
            payload.timestamp_end = clamp(
                Math.ceil(endTime),
                start + 1,
                hasDuration ? Math.ceil(duration) : Infinity
            );
        }

        try {
            await onCreateComment({ ...payload });
            setComment(initialComment);
            setPicked([]);
            onCancelTime();
        } catch (error) {
            toast.error((error as Error).message);
        }
    };

    const onRangeChange = (start: number, end: number) => {
        setStartTime(start);
        setEndTime(end);
    };

    const handleTimeBtnClick = () => {
        const videoEl = videoRef.current;
        if (!videoEl || !hasDuration) return;
        // Seed proportionally to the video length (3–8s); the user drags from there.
        const seedLength = clamp(duration * 0.1, 3, 8);
        const start = clamp(videoEl.currentTime, 0, Math.max(duration - MIN_RANGE_SECONDS, 0));
        const end = clamp(start + seedLength, Math.min(start + MIN_RANGE_SECONDS, duration), duration);
        setStartTime(start);
        setEndTime(end);
        setIsTimestampActive(true);
    };

    const isCreateBtnDisabled = isCommentLoading || !comment?.message;
    const isTimestampBtnDisabled = !hasDuration;
    return (
        <Box className="border border-border-token rounded-2xl p-4 flex flex-col gap-4">
            {isTimestampActive && hasDuration && (
                <VideoTimeline
                    video={videoRef.current}
                    videoUrl={videoRef.current?.currentSrc || undefined}
                    duration={duration}
                    rangeStart={startTime}
                    rangeEnd={endTime}
                    onRangeChange={onRangeChange}
                    minRange={MIN_RANGE_SECONDS}
                />
            )}
            {isGuestMode && (
                <input
                    type="text"
                    value={guestName || ""}
                    onChange={(e) => onGuestNameChange!(e.target.value)}
                    className="bg-surface-2 p-3 rounded-xl w-full outline-none"
                    placeholder="Your name (optional)"
                />
            )}
            <MentionTextarea
                value={comment.message || ""}
                onChange={(message) => editComment({ message })}
                usersUrl={
                    !isGuestMode && project?.id
                        ? commentTaggableUsers(project.id)
                        : null
                }
                onMentionPicked={(taggedUser) =>
                    setPicked((prev) =>
                        prev.some((p) => p.userid === taggedUser.userid)
                            ? prev
                            : [...prev, taggedUser],
                    )
                }
                className="bg-surface-2 p-3 rounded-xl resize-none w-full outline-none"
                placeholder="Add general or time stamp specific comments & feedback here"
            />
            <Box className="flex items-center justify-between w-full">
                {isTimestampActive ? (
                    <Box className="flex gap-1">
                        <Box className="flex gap-2 items-center py-1.5 px-3 border border-border-token rounded-2xl">
                            <Box className="flex items-center justify-center">
                                <IconClock size={24} />
                            </Box>
                            <Typography className="whitespace-nowrap">
                                {toMmSs(startTime)} → {toMmSs(endTime)}
                            </Typography>
                        </Box>
                        <Box
                            className="flex p-0.5 bg-surface hover:bg-surface-2 cursor-pointer h-fit rounded-2xl"
                            onClick={onCancelTime}
                        >
                            <IconCross width={18} height={18} />
                        </Box>
                    </Box>
                ) : (
                    <Button
                        disabled={isTimestampBtnDisabled}
                        icon={<IconTimer />}
                        variant="soft"
                        color="default"
                        className="!pl-3 !pr-4 !gap-1.5 h-[40px] !flex !items-center"
                        onClick={handleTimeBtnClick}
                    >
                        Add Timestamp
                    </Button>
                )}

                <Box className="flex gap-4 items-center">
                    <FeedbackCriteria />
                    <Button
                        disabled={isCreateBtnDisabled}
                        className={classNames("!px-3 h-[40px] !flex !items-center !min-w-fit", {
                            "!bg-tint": isCreateBtnDisabled
                        })}
                        onClick={submitComment}
                    >
                        {isCommentLoading ? "Saving..." : "Save Comment"}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};
